import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API_URL from './config';
import {
  getStoredToken,
  setStoredToken,
  removeStoredToken,
  setStoredUserId,
  isRemembered,
} from './utils/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => getStoredToken());
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    removeStoredToken();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  }, []);

  const fetchUserData = useCallback(async (tokenValue) => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${tokenValue}` },
      });

      if (!res.ok) {
        console.warn("Session expirée ou token invalide");
        logout();
        return null;
      }

      const userData = await res.json();
      setUser(userData);
      setStoredUserId(userData.id, isRemembered());
      setIsAuthenticated(true);
      return userData;
    } catch (err) {
      console.error("Erreur lors de la récupération des infos utilisateur :", err);
      logout();
      return null;
    }
  }, [logout]);

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      const storedToken = getStoredToken();
      if (!storedToken) {
        if (isMounted) {
          setIsAuthenticated(false);
          setUser(null);
          setToken(null);
          setIsLoading(false);
        }
        return;
      }

      if (isMounted) {
        setToken(storedToken);
      }

      try {
        const res = await fetch(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });

        if (!res.ok) {
          console.warn("Session expirée ou token invalide (init)");
          if (isMounted) {
            removeStoredToken();
            setToken(null);
            setUser(null);
            setIsAuthenticated(false);
          }
          return;
        }

        const userData = await res.json();
        if (isMounted) {
          setUser(userData);
          setStoredUserId(userData.id, isRemembered());
          setIsAuthenticated(true);
        }
      } catch (err) {
        console.error("Erreur lors de l'initialisation de l'authentification :", err);
        if (isMounted) {
          removeStoredToken();
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (tokenValue, rememberMe = false) => {
    setIsLoading(true);
    setStoredToken(tokenValue, rememberMe);
    setToken(tokenValue);

    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${tokenValue}` },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || "Impossible de récupérer les informations utilisateur.");
      }

      const userData = await res.json();
      setUser(userData);
      setStoredUserId(userData.id, rememberMe);
      setIsAuthenticated(true);
      return userData;
    } catch (err) {
      removeStoredToken();
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const isManager           = user?.role === 'MANAGER';
  const isTeamLeader        = user?.role === 'TEAM_LEADER';
  const isTeamLeaderOrAbove = ['MANAGER', 'TEAM_LEADER'].includes(user?.role);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        isLoading,
        login,
        logout,
        fetchUserData,
        isManager,
        isTeamLeader,
        isTeamLeaderOrAbove,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
